import axios, { type AxiosInstance, type AxiosRequestConfig } from "axios";
import { useContext, useEffect, useState } from "react";
import { GlobalRequestErrorsContext } from "../config/app-config";
import { getAuthenticatedAxios } from "./authentication";

export type RequestState<T> = [
  result: T | undefined,
  error: Error | undefined,
  isLoading: boolean,
];
export const useAuthenticatedRequest = <T>(
  url: string,
  config?: AxiosRequestConfig,
  shouldLoad = true,
): RequestState<T> => {
  const { axios } = getAuthenticatedAxios();
  return useRequest(url, axios, config, shouldLoad);
};

export const useRequest = <T>(
  url: string,
  axios: AxiosInstance,
  config?: AxiosRequestConfig,
  shouldLoad = true,
): RequestState<T> => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error>();
  const [result, setResult] = useState<T>();

  const requestErrorsContext = useContext(GlobalRequestErrorsContext);
  const { setNetworkError } = requestErrorsContext;

  useEffect(() => {
    if (!shouldLoad) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setResult(undefined);
    setError(undefined);
    const controller = new AbortController();
    axios
      .get<T>(url, { ...config, signal: controller.signal })
      .then((res) => {
        setIsLoading(false);
        setResult(res.data);
        setNetworkError(false);
      })
      .catch((e) => {
        if (e.name !== "CanceledError") {
          setIsLoading(false);
          setError(e);
        }
        if (!e.status && e.message === "Network Error") {
          setNetworkError(true);
        }
      });
    return () => controller.abort();
  }, [setNetworkError, axios, url, config, shouldLoad]);
  return [result, error, isLoading];
};

interface PostOptions extends AxiosRequestConfig {
  url: string;
  data?: any;
}

export const usePost = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<any>(null);

  const post = async ({ url, data, ...config }: PostOptions) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(url, data, config);
      setResponse(res.data);
    } catch (err: any) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return { post, loading, error, response };
};
